import type { DeviceOption } from '../lib/media.ts';
import { Icon } from './Icon.tsx';
import { Modal } from './Modal.tsx';

interface DeviceSheetProps {
  cameras: DeviceOption[];
  mics: DeviceOption[];
  /** Gerät, das gerade läuft. null, wenn der Browser es nicht verrät. */
  cameraId: string | null;
  micId: string | null;
  sound: boolean;
  busy: 'video' | 'audio' | null;
  problem: string | null;
  onPick: (kind: 'video' | 'audio', id: string) => void;
  onSwitchCamera: () => void;
  onToggleSound: (on: boolean) => void;
  onClose: () => void;
}

export function DeviceSheet({
  cameras,
  mics,
  cameraId,
  micId,
  sound,
  busy,
  problem,
  onPick,
  onSwitchCamera,
  onToggleSound,
  onClose,
}: DeviceSheetProps) {
  return (
    <Modal title="Kamera und Ton" onClose={onClose} sheet>
      {cameras.length > 1 && (
        <button
          type="button"
          className="button button--primary"
          onClick={onSwitchCamera}
          disabled={busy !== null}
        >
          <Icon name="switchCam" size={18} /> Kamera wechseln
        </button>
      )}

      <DeviceGroup
        title="Kamera"
        icon="cam"
        options={cameras}
        activeId={cameraId}
        busy={busy === 'video'}
        empty="Wir sehen gerade keine Kamera."
        onPick={(id) => onPick('video', id)}
      />

      <DeviceGroup
        title="Mikrofon"
        icon="mic"
        options={mics}
        activeId={micId}
        busy={busy === 'audio'}
        empty="Wir sehen gerade kein Mikrofon."
        onPick={(id) => onPick('audio', id)}
      />

      <section className="filter-group">
        <h3>
          <Icon name="sound" size={18} /> Ton
        </h3>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={sound}
            onChange={(event) => onToggleSound(event.target.checked)}
          />
          <span>Kurzer Ton, sobald jemand da ist</span>
        </label>
      </section>

      {problem && <p className="alert">{problem}</p>}
      <p className="hint">Die Auswahl gilt sofort, auch mitten im Gespräch.</p>
      <button type="button" className="button button--primary" onClick={onClose}>
        Fertig
      </button>
    </Modal>
  );
}

function DeviceGroup({
  title,
  icon,
  options,
  activeId,
  busy,
  empty,
  onPick,
}: {
  title: string;
  icon: 'cam' | 'mic';
  options: DeviceOption[];
  activeId: string | null;
  busy: boolean;
  empty: string;
  onPick: (id: string) => void;
}) {
  return (
    <section className="filter-group">
      <h3>
        <Icon name={icon} size={18} /> {title}
        {busy && <span className="spinner spinner--small" aria-label="wird umgestellt" />}
      </h3>
      {options.length === 0 ? (
        <p className="hint">{empty}</p>
      ) : (
        <div className="options options--list">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="option"
              aria-pressed={option.id === activeId}
              disabled={busy}
              onClick={() => onPick(option.id)}
            >
              <span className="option__name">{option.label}</span>
              {option.id === activeId && (
                <span className="option__check">
                  <Icon name="check" size={14} />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
